/**
 * Hook useAuth
 * Hook personalizado para gestionar autenticación de usuarios
 * Implementa patrón singleton para evitar conflictos de locks
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { ERROR_MESSAGES } from '../constants/subscriptions';

// Estado global singleton para autenticación
let globalAuthState = {
  user: null,
  session: null,
  loading: true,
  error: null,
  listeners: new Set(),
  subscription: null,
  initialized: false,
  initializing: false // Flag para prevenir inicializaciones simultáneas
};

/**
 * Hook para gestionar autenticación
 * @param {Object} options - Opciones adicionales
 */
export const useAuth = (options = {}) => {
  const { autoRefresh = true } = options;

  const [user, setUser] = useState(globalAuthState.user);
  const [session, setSession] = useState(globalAuthState.session);
  const [loading, setLoading] = useState(globalAuthState.loading);
  const [error, setError] = useState(globalAuthState.error);

  /**
   * Actualiza el estado global y notifica a todos los listeners
   */
  const updateGlobalState = useCallback((updates) => {
    globalAuthState = { ...globalAuthState, ...updates };
    globalAuthState.listeners.forEach(listener => listener(updates));
  }, []);

  /**
   * Inicia sesión con email y contraseña
   */
  const signIn = useCallback(async (email, password) => {
    try {
      updateGlobalState({ loading: true, error: null });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      updateGlobalState({ user: data.user, loading: false });
      return { success: true, user: data.user };
    } catch (err) {
      const errorMessage = err.message || ERROR_MESSAGES.GENERIC_ERROR;
      updateGlobalState({ error: errorMessage, loading: false });
      return { success: false, error: errorMessage };
    }
  }, [updateGlobalState]);

  /**
   * Registra un nuevo usuario
   */
  const signUp = useCallback(async (email, password, metadata = {}) => {
    try {
      updateGlobalState({ loading: true, error: null });

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
        },
      });

      if (error) throw error;

      updateGlobalState({ user: data.user, loading: false });
      return { success: true, user: data.user };
    } catch (err) {
      const errorMessage = err.message || ERROR_MESSAGES.GENERIC_ERROR;
      updateGlobalState({ error: errorMessage, loading: false });
      return { success: false, error: errorMessage };
    }
  }, [updateGlobalState]);

  /**
   * Cierra sesión
   */
  const signOut = useCallback(async () => {
    try {
      updateGlobalState({ loading: true, error: null });

      const { error } = await supabase.auth.signOut();

      if (error) throw error;

      updateGlobalState({ user: null, loading: false });
      return { success: true };
    } catch (err) {
      const errorMessage = err.message || ERROR_MESSAGES.GENERIC_ERROR;
      updateGlobalState({ error: errorMessage, loading: false });
      return { success: false, error: errorMessage };
    }
  }, [updateGlobalState]);

  /**
   * Restablece contraseña
   * Envía un email de recuperación con un link para restablecer la contraseña
   * @param {string} email - Email del usuario
   * @param {string} redirectTo - URL a donde redirigir después de hacer click en el link
   */
  const resetPassword = useCallback(async (email, redirectTo) => {
    try {
      updateGlobalState({ error: null });

      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectTo || `${window.location.origin}/auth/reset-password`,
      });

      if (error) throw error;

      return { success: true };
    } catch (err) {
      const errorMessage = err.message || ERROR_MESSAGES.GENERIC_ERROR;
      updateGlobalState({ error: errorMessage });
      return { success: false, error: errorMessage };
    }
  }, [updateGlobalState]);

  /**
   * Actualiza el perfil del usuario
   */
  const updateProfile = useCallback(async (metadata) => {
    try {
      updateGlobalState({ loading: true, error: null });

      const { data, error } = await supabase.auth.updateUser({
        data: metadata,
      });

      if (error) throw error;

      updateGlobalState({ user: data.user, loading: false });
      return { success: true, user: data.user };
    } catch (err) {
      const errorMessage = err.message || ERROR_MESSAGES.GENERIC_ERROR;
      updateGlobalState({ error: errorMessage, loading: false });
      return { success: false, error: errorMessage };
    }
  }, [updateGlobalState]);

  /**
   * Inicia sesión con Google
   */
  const signInWithGoogle = useCallback(async () => {
    try {
      updateGlobalState({ loading: true, error: null });

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) throw error;

      return { success: true, data };
    } catch (err) {
      const errorMessage = err.message || ERROR_MESSAGES.GENERIC_ERROR;
      updateGlobalState({ error: errorMessage, loading: false });
      return { success: false, error: errorMessage };
    }
  }, [updateGlobalState]);

  /**
   * Verifica si el usuario está autenticado
   */
  const isAuthenticated = !!user;

  /**
   * Obtiene el ID del usuario
   */
  const getUserId = useCallback(() => {
    return user?.id || null;
  }, [user]);

  /**
   * Obtiene el email del usuario
   */
  const getUserEmail = useCallback(() => {
    return user?.email || null;
  }, [user]);

  /**
   * Obtiene los metadatos del usuario
   */
  const getUserMetadata = useCallback(() => {
    return user?.user_metadata || {};
  }, [user]);

  /**
   * Listener para cambios en el estado global
   * Solo actualiza el estado local si el valor realmente cambió
   */
  useEffect(() => {
    const listener = (updates) => {
      // Solo actualizar si el valor realmente cambió
      if (updates.user !== undefined && updates.user !== user) setUser(updates.user);
      if (updates.session !== undefined && updates.session !== session) setSession(updates.session);
      if (updates.loading !== undefined && updates.loading !== loading) setLoading(updates.loading);
      if (updates.error !== undefined && updates.error !== error) setError(updates.error);
    };

    globalAuthState.listeners.add(listener);

    // Sincronizar estado inicial
    setUser(globalAuthState.user);
    setSession(globalAuthState.session);
    setLoading(globalAuthState.loading);
    setError(globalAuthState.error);

    return () => {
      globalAuthState.listeners.delete(listener);
    };
  }, []);

  /**
   * Efecto para inicializar la sesión global (solo una vez)
   */
  useEffect(() => {
    // Si ya está inicializado o inicializándose, no hacer nada
    if (globalAuthState.initialized || globalAuthState.initializing) return;

    const initializeAuth = async () => {
      // Marcar como inicializando para prevenir duplicados
      globalAuthState.initializing = true;

      try {
        const { data: { session }, error } = await supabase.auth.getSession();

        if (error) throw error;

        updateGlobalState({
          user: session?.user || null,
          session: session || null,
          loading: false,
          initialized: true,
          initializing: false
        });
      } catch (err) {
        console.error('Error obteniendo sesión:', err);
        updateGlobalState({
          error: err.message,
          loading: false,
          initialized: true,
          initializing: false
        });
      }
    };

    initializeAuth();
  }, [updateGlobalState]);

  /**
   * Efecto para escuchar cambios de autenticación (solo una vez globalmente)
   */
  useEffect(() => {
    if (!autoRefresh || globalAuthState.subscription) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        updateGlobalState({
          user: session?.user || null,
          session: session || null,
          loading: false
        });

        if (event === 'SIGNED_OUT') {
          updateGlobalState({ user: null, session: null });
        }
      }
    );

    globalAuthState.subscription = subscription;

    return () => {
      // Solo limpiar si esta es la suscripción actual (prevenir limpieza en React Strict Mode)
      if (globalAuthState.subscription === subscription) {
        subscription.unsubscribe();
        globalAuthState.subscription = null;
      }
    };
  }, [autoRefresh, updateGlobalState]);

  // Memoizar el valor de retorno para evitar re-renders innecesarios
  const authValue = useMemo(() => ({
    // Datos
    user,
    session,
    loading,
    error,
    isAuthenticated,

    // Métodos
    signIn,
    signUp,
    signOut,
    resetPassword,
    updateProfile,
    signInWithGoogle,

    // Utilidades
    getUserId,
    getUserEmail,
    getUserMetadata
  }), [user, session, loading, error, isAuthenticated, signIn, signUp, signOut, resetPassword, updateProfile, signInWithGoogle, getUserId, getUserEmail, getUserMetadata]);

  return authValue;
};

export default useAuth;
